import { sqliteTable, text, integer, real, uniqueIndex, index } from "drizzle-orm/sqlite-core";
import { relations, sql } from "drizzle-orm";
import type { Segment } from "./welya";
import type { MatchMethod } from "./matching";

export const PRODUCT_KEYS = ["power_coaching", "accelerateur"] as const;
export type ProductKey = (typeof PRODUCT_KEYS)[number];

export const PROSPECT_STATUSES = [
  "a_contacter",
  "en_discussion",
  "relance",
  "closed_won",
  "closed_lost",
  "rembourse",
] as const;
export type ProspectStatus = (typeof PROSPECT_STATUSES)[number];

export const RDV_OUTCOMES = ["attended", "no_show"] as const;
export type RdvOutcome = (typeof RDV_OUTCOMES)[number];

export const INTERACTION_KINDS = [
  "note",
  "appel",
  "message",
  "email",
  "rdv",
  "relance",
] as const;
export type InteractionKind = (typeof INTERACTION_KINDS)[number];

export const PAYMENT_STATUSES = ["due", "paye", "rembourse"] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

export const COMMISSION_STATUSES = [
  "en_attente_retractation",
  "du",
  "paye",
  "annule",
] as const;
export type CommissionStatus = (typeof COMMISSION_STATUSES)[number];

export const prospects = sqliteTable("prospects", {
  id: integer("id").primaryKey({ autoIncrement: true }),
  firstName: text("first_name").notNull(),
  lastName: text("last_name"),
  email: text("email"),
  phone: text("phone"),
  source: text("source"),
  status: text("status").$type<ProspectStatus>().notNull().default("a_contacter"),
  productInterest: text("product_interest").$type<ProductKey | null>(),
  context: text("context"),
  nextFollowUpAt: text("next_follow_up_at"),
  lastContactAt: text("last_contact_at"),
  segment: text("segment").$type<Segment | null>(),
  nextAction: text("next_action"),
  nextActionSetAt: text("next_action_set_at"),
  lossReason: text("loss_reason"),
  briefNotes: text("brief_notes"),
  createdAt: text("created_at")
    .notNull()
    .default(sql`(CURRENT_TIMESTAMP)`),
  updatedAt: text("updated_at")
    .notNull()
    .default(sql`(CURRENT_TIMESTAMP)`),
});

export const sales = sqliteTable("sales", {
  id: integer("id").primaryKey({ autoIncrement: true }),
  prospectId: integer("prospect_id")
    .notNull()
    .references(() => prospects.id, { onDelete: "cascade" }),
  product: text("product").$type<ProductKey>().notNull(),
  totalAmount: real("total_amount").notNull(),
  commissionRate: real("commission_rate").notNull().default(0.1),
  closedAt: text("closed_at").notNull(),
  retractationEndsAt: text("retractation_ends_at").notNull(),
  paymentPlan: integer("payment_plan").notNull().default(1),
  status: text("status")
    .$type<"actif" | "rembourse" | "annule">()
    .notNull()
    .default("actif"),
  notes: text("notes"),
  createdAt: text("created_at")
    .notNull()
    .default(sql`(CURRENT_TIMESTAMP)`),
  updatedAt: text("updated_at")
    .notNull()
    .default(sql`(CURRENT_TIMESTAMP)`),
});

export const payments = sqliteTable("payments", {
  id: integer("id").primaryKey({ autoIncrement: true }),
  saleId: integer("sale_id")
    .notNull()
    .references(() => sales.id, { onDelete: "cascade" }),
  installmentIndex: integer("installment_index").notNull(),
  amount: real("amount").notNull(),
  commissionAmount: real("commission_amount").notNull(),
  dueDate: text("due_date").notNull(),
  paidDate: text("paid_date"),
  status: text("status").$type<PaymentStatus>().notNull().default("due"),
  commissionStatus: text("commission_status")
    .$type<CommissionStatus>()
    .notNull()
    .default("en_attente_retractation"),
  commissionPaidDate: text("commission_paid_date"),
  createdAt: text("created_at")
    .notNull()
    .default(sql`(CURRENT_TIMESTAMP)`),
});

export const interactions = sqliteTable("interactions", {
  id: integer("id").primaryKey({ autoIncrement: true }),
  prospectId: integer("prospect_id")
    .notNull()
    .references(() => prospects.id, { onDelete: "cascade" }),
  kind: text("kind").$type<InteractionKind>().notNull().default("note"),
  body: text("body").notNull(),
  occurredAt: text("occurred_at")
    .notNull()
    .default(sql`(CURRENT_TIMESTAMP)`),
  createdAt: text("created_at")
    .notNull()
    .default(sql`(CURRENT_TIMESTAMP)`),
});

export const prospectsRelations = relations(prospects, ({ many }) => ({
  sales: many(sales),
  interactions: many(interactions),
}));

export const salesRelations = relations(sales, ({ one, many }) => ({
  prospect: one(prospects, {
    fields: [sales.prospectId],
    references: [prospects.id],
  }),
  payments: many(payments),
}));

export const paymentsRelations = relations(payments, ({ one }) => ({
  sale: one(sales, {
    fields: [payments.saleId],
    references: [sales.id],
  }),
}));

export const interactionsRelations = relations(interactions, ({ one }) => ({
  prospect: one(prospects, {
    fields: [interactions.prospectId],
    references: [prospects.id],
  }),
}));

export const googleTokens = sqliteTable("google_tokens", {
  id: integer("id").primaryKey(),
  refreshToken: text("refresh_token").notNull(),
  accessToken: text("access_token"),
  accessTokenExpiresAt: text("access_token_expires_at"),
  calendarId: text("calendar_id").notNull().default("primary"),
  filterPattern: text("filter_pattern").notNull().default("Power Coaching"),
  userEmail: text("user_email"),
  connectedAt: text("connected_at")
    .notNull()
    .default(sql`(CURRENT_TIMESTAMP)`),
  lastSyncAt: text("last_sync_at"),
  lastSyncError: text("last_sync_error"),
  lastSyncCount: integer("last_sync_count").default(0),
});

export const googleEvents = sqliteTable("google_events", {
  id: integer("id").primaryKey({ autoIncrement: true }),
  eventId: text("event_id").notNull().unique(),
  summary: text("summary"),
  description: text("description"),
  startTime: text("start_time").notNull(),
  endTime: text("end_time"),
  attendeeEmail: text("attendee_email"),
  attendeeName: text("attendee_name"),
  organizerEmail: text("organizer_email"),
  meetingUrl: text("meeting_url"),
  status: text("status")
    .$type<"confirmed" | "cancelled">()
    .notNull()
    .default("confirmed"),
  prospectId: integer("prospect_id").references(() => prospects.id, {
    onDelete: "set null",
  }),
  outcome: text("outcome").$type<RdvOutcome | null>(),
  rawJson: text("raw_json"),
  createdAt: text("created_at")
    .notNull()
    .default(sql`(CURRENT_TIMESTAMP)`),
  updatedAt: text("updated_at")
    .notNull()
    .default(sql`(CURRENT_TIMESTAMP)`),
});

export const googleEventsRelations = relations(googleEvents, ({ one }) => ({
  prospect: one(prospects, {
    fields: [googleEvents.prospectId],
    references: [prospects.id],
  }),
}));

export const PRESTATION_STATUSES = ["facture", "paye", "annule"] as const;
export type PrestationStatus = (typeof PRESTATION_STATUSES)[number];

export const prestations = sqliteTable("prestations", {
  id: integer("id").primaryKey({ autoIncrement: true }),
  clientName: text("client_name").notNull(),
  clientEmail: text("client_email"),
  description: text("description"),
  amount: real("amount").notNull(),
  invoicedAt: text("invoiced_at").notNull(),
  dueAt: text("due_at"),
  paidAt: text("paid_at"),
  status: text("status").$type<PrestationStatus>().notNull().default("facture"),
  notes: text("notes"),
  createdAt: text("created_at")
    .notNull()
    .default(sql`(CURRENT_TIMESTAMP)`),
  updatedAt: text("updated_at")
    .notNull()
    .default(sql`(CURRENT_TIMESTAMP)`),
});

export type Prospect = typeof prospects.$inferSelect;
export type NewProspect = typeof prospects.$inferInsert;
export type Sale = typeof sales.$inferSelect;
export type NewSale = typeof sales.$inferInsert;
export type Payment = typeof payments.$inferSelect;
export type NewPayment = typeof payments.$inferInsert;
export type Interaction = typeof interactions.$inferSelect;
export type NewInteraction = typeof interactions.$inferInsert;
export type GoogleToken = typeof googleTokens.$inferSelect;
export type NewGoogleToken = typeof googleTokens.$inferInsert;
export type GoogleEvent = typeof googleEvents.$inferSelect;
export type NewGoogleEvent = typeof googleEvents.$inferInsert;
export type Prestation = typeof prestations.$inferSelect;
export type NewPrestation = typeof prestations.$inferInsert;

export const todos = sqliteTable("todos", {
  id: integer("id").primaryKey({ autoIncrement: true }),
  title: text("title").notNull(),
  dueAt: text("due_at"),
  done: integer("done", { mode: "boolean" }).notNull().default(false),
  completedAt: text("completed_at"),
  createdAt: text("created_at")
    .notNull()
    .default(sql`(CURRENT_TIMESTAMP)`),
  updatedAt: text("updated_at")
    .notNull()
    .default(sql`(CURRENT_TIMESTAMP)`),
});

export type Todo = typeof todos.$inferSelect;
export type NewTodo = typeof todos.$inferInsert;

export const callSummaries = sqliteTable(
  "call_summaries",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    prospectId: integer("prospect_id")
      .notNull()
      .references(() => prospects.id, { onDelete: "cascade" }),
    source: text("source", { enum: ["fireflies", "fathom"] }).notNull(),
    externalId: text("external_id").notNull(),
    recordingUrl: text("recording_url").notNull(),
    callAt: text("call_at").notNull(),
    label: text("label", { enum: ["chaud", "timing", "argent", "froid"] }).notNull(),
    phrase: text("phrase").notNull(),
    createdAt: text("created_at")
      .notNull()
      .default(sql`(CURRENT_TIMESTAMP)`),
  },
  (t) => [
    uniqueIndex("call_summaries_source_external_uq").on(t.source, t.externalId),
    index("call_summaries_prospect_idx").on(t.prospectId),
  ],
);

export type CallSummaryRow = typeof callSummaries.$inferSelect;
export type NewCallSummaryRow = typeof callSummaries.$inferInsert;

export const webinars = sqliteTable(
  "webinars",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    title: text("title").notNull(),
    /** Date du live, ISO (YYYY-MM-DD) */
    heldAt: text("held_at").notNull(),
    sheetId: text("sheet_id").notNull(),
    sheetGid: text("sheet_gid").notNull(),
    sheetUrl: text("sheet_url").notNull(),
    importedAt: text("imported_at")
      .notNull()
      .default(sql`(CURRENT_TIMESTAMP)`),
    statsJson: text("stats_json"),
  },
  (t) => [uniqueIndex("webinars_sheet_held_uq").on(t.sheetId, t.sheetGid, t.heldAt)],
);

export const webinarAttendees = sqliteTable(
  "webinar_attendees",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    webinarId: integer("webinar_id")
      .notNull()
      .references(() => webinars.id, { onDelete: "cascade" }),
    prospectId: integer("prospect_id").references(() => prospects.id, {
      onDelete: "set null",
    }),
    email: text("email").notNull(),
    firstName: text("first_name").notNull(),
    phone: text("phone"),
    segment: text("segment").$type<Segment>().notNull(),
    score: real("score").notNull().default(0),
    completion: real("completion").notNull().default(0),
    sawOffer: integer("saw_offer", { mode: "boolean" }).notNull().default(false),
    clickedOffer: integer("clicked_offer", { mode: "boolean" })
      .notNull()
      .default(false),
    converted: integer("converted", { mode: "boolean" }).notNull().default(false),
    enteredAt: text("entered_at"),
    watchTime: text("watch_time"),
    /** Messages du chat joints par "\n" */
    chat: text("chat"),
    city: text("city"),
    matchMethod: text("match_method").$type<MatchMethod>().notNull().default("new"),
    createdAt: text("created_at")
      .notNull()
      .default(sql`(CURRENT_TIMESTAMP)`),
    updatedAt: text("updated_at")
      .notNull()
      .default(sql`(CURRENT_TIMESTAMP)`),
  },
  (t) => [uniqueIndex("webinar_attendees_webinar_email_uq").on(t.webinarId, t.email)],
);

export const settings = sqliteTable("settings", {
  key: text("key").primaryKey(),
  value: text("value").notNull(),
});

export const webinarsRelations = relations(webinars, ({ many }) => ({
  attendees: many(webinarAttendees),
}));

export const webinarAttendeesRelations = relations(webinarAttendees, ({ one }) => ({
  webinar: one(webinars, {
    fields: [webinarAttendees.webinarId],
    references: [webinars.id],
  }),
  prospect: one(prospects, {
    fields: [webinarAttendees.prospectId],
    references: [prospects.id],
  }),
}));

export const SETTINGS_DEFAULTS = {
  owner_emails: "@christophedang.com,dangchristophe@hotmail.com",
  calendly_booking_url:
    "https://calendly.com/dangchristophe_powercoaching/actionpc",
} as const;

export type Webinar = typeof webinars.$inferSelect;
export type NewWebinar = typeof webinars.$inferInsert;
export type WebinarAttendee = typeof webinarAttendees.$inferSelect;
export type NewWebinarAttendee = typeof webinarAttendees.$inferInsert;
export type Setting = typeof settings.$inferSelect;
export type NewSetting = typeof settings.$inferInsert;
